import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  createContext,
} from 'react'
import { Switch, Route } from 'react-router-dom'
import {
  mdiLinkVariant,
  mdiCogOutline,
  mdiMagnify,
  mdiStarOutline,
  mdiInformationOutline,
  mdiHelpCircleOutline,
  mdiLogout,
  mdiLogin,
} from '@mdi/js'

import { Header, Footer, Icon, Debug } from '@jcu/cookbook-react'

import config from '../config'
import { AuthService, onLoginClick, checkAccess } from '../lib/auth'
import { createOrcidLinkHandler } from '../lib/orcid'
import Home from './Home'
import Settings from './Settings'
import Search, { allowedRoles as searchRoles } from './Search'
import Admin, { allowedRoles as adminRoles } from './Admin'
import About from './About'
import Help from './Help'
import NotFound from './NotFound'
import './App.css'
import { useAlertHandler } from '../lib/alert'
import PropTypes from 'prop-types'
import useRequester from '../lib/requester'
import { Container, Alert } from 'react-bootstrap'

// Use this function to reduce test boilerplate
const testFunction = (fn, ...args) => {
  if (fn) return fn(...args)
}

const ApplicationContext = createContext(undefined)

const App = ({
  defaultUser,
  defaultOrcid,
  defaultAlertHandler,
  defaultAuth,
  _checkOrcidState,
  _checkLoginState,
  _handleLoginMessage,
  _logoutHandler,
  _handleOrcidMessage,
  _unlinkOrcid,
  defaultRequester,
}) => {
  // States
  const [user, setUser] = useState(
    defaultUser || JSON.parse(localStorage.getItem('_user')) || {}
  )
  const [orcid, setOrcid] = useState(
    defaultOrcid || JSON.parse(localStorage.getItem('_orcid')) || {}
  )

  const alertHandlerHook = useAlertHandler()
  const alertHandler = defaultAlertHandler || alertHandlerHook
  const { clearAlerts, displayAlert } = alertHandler

  const auth = useRef(defaultAuth || new AuthService())
  const isMounted = useRef(true)

  const logoutCallback = useCallback(() => {
    setState(setUser, {})
    setState(setOrcid, {})
  }, [])

  const requesterHook = useRequester({
    authService: auth.current,
    alertHandler,
    logoutCallback,
  })

  const requester = defaultRequester || requesterHook
  const { request, logout } = requester

  const setState = (setStateFn, ...args) => {
    if (!isMounted.current) return // We are unmounted, so we don't want to update state
    setStateFn(...args)
  }

  const checkOrcidState = useCallback(
    async (options = { clearAlerts: false }) => {
      testFunction(_checkOrcidState, options)
      const { data } = await request('/orcid/me')

      if (data) {
        setState(setOrcid, data)
        if (options.clearAlerts) clearAlerts()
      }
    },
    [clearAlerts, _checkOrcidState, request]
  )

  const checkLoginState = useCallback(
    async (options = { clearAlerts: false, ignoreErrors: false }) => {
      testFunction(_checkLoginState, options)
      const { data } = await request('/user/me')
      if (data) {
        checkOrcidState(options)
        setState(setUser, data)
        if (options.clearAlerts) clearAlerts()
      } else {
        auth.current.logout()
      }
    },
    [checkOrcidState, clearAlerts, request, _checkLoginState]
  )

  const handleLoginMessage = useCallback(
    event => {
      if (event.origin !== config.urls.messageOrigin) {
        testFunction(_handleLoginMessage, 'invalid origin')
        return
      }
      testFunction(_handleLoginMessage, event, auth.current)

      if (
        event.data &&
        typeof event.data === 'object' &&
        (event.data.type === 'jcu.cas.login' ||
          event.data.type === 'jcu.oidc.login')
      ) {
        if (auth.current.getToken()) {
          displayAlert({
            type: 'warning',
            message:
              'Your login session had expired while you were away. You are now logged in again.',
          })
          auth.current.login(event.data.token)
          checkLoginState({ clearAlerts: false })
        } else {
          auth.current.login(event.data.token)
          checkLoginState()
        }
      }
    },
    [checkLoginState, displayAlert, _handleLoginMessage]
  )

  const logoutHandler = useCallback(async () => {
    testFunction(_logoutHandler)
    await request('/user/logout', {
      fetchOptions: {
        credentials: 'include',
      },
    })
    logout()
  }, [logout, request, _logoutHandler])

  const handleLogoutClick = (event, options) => {
    event.preventDefault()
    alertHandler.clearAlerts()
    alertHandler.displayAlert({
      type: 'info',
      message: (
        <>
          <Alert.Heading>You have been successfully logged out</Alert.Heading>
          {!options?.force && !user.id_token
            ? 'You will remain logged into other JCU services until you close your browser.'
            : 'You have been logged out of all JCU services'}
        </>
      ),
    })

    logoutHandler()
    if (options?.force && user.id_token)
      // Backend needs to send id_token in user JWT
      fetch(
        `https://sso.jcu.edu.au/openam/oauth2/connect/endSession?id_token_hint=${user.id_token}`
      )
  }

  const handleOrcidMessage = useCallback(
    event => {
      if (event.origin !== config.urls.messageOrigin) {
        testFunction(_handleOrcidMessage, 'invalid origin')
        return
      }

      if (event.data && typeof event.data === 'object') {
        if (event.data.type === 'orcid.connect') {
          checkOrcidState()
        } else if (event.data.type === 'orcid.already_linked') {
          displayAlert({
            type: 'danger',
            message:
              'This ORCID iD is already validated and linked to your JCU identity. If the current ORCID iD is not yours, you must unlink it first under Settings.',
          })
          checkOrcidState({ clearAlerts: false })
        } else if (event.data.type === 'orcid.access_denied') {
          displayAlert({
            type: 'danger',
            message: 'You must authorise the ORCID iD linkage to proceed.',
          })
        }
      }
    },
    [checkOrcidState, displayAlert, _handleOrcidMessage]
  )

  const unlinkOrcid = async orcidToken => {
    testFunction(_unlinkOrcid, orcidToken)
    const { data } = await request(
      '/orcid/unlink' + (orcidToken ? '/' + orcidToken.id : '')
    )
    if (data) {
      if (!orcidToken) {
        // If we unlinked everything, clear the ORCID and alert
        setState(setOrcid, {})
        displayAlert({
          type: 'success',
          message: 'Your ORCID iD was unlinked.',
        })
      } else {
        checkOrcidState()
      }
    }
  }

  // Effects
  // componentDidMount
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  // On Unload Save User Data to Local Storage
  // JWT is also already saved but this allows for refreshes
  useEffect(() => {
    localStorage.setItem('_user', JSON.stringify(user))
    localStorage.setItem('_orcid', JSON.stringify(orcid))
  }, [user, orcid])

  useEffect(() => {
    checkLoginState({ ignoreErrors: true })
    window.addEventListener('message', handleLoginMessage)
    window.addEventListener('message', handleOrcidMessage)

    // This Function Will Be Called on Unmount
    return () => {
      window.removeEventListener('message', handleLoginMessage)
      window.removeEventListener('message', handleOrcidMessage)
    }
  }, [checkLoginState, handleLoginMessage, handleOrcidMessage])

  return (
    <div className="App d-flex flex-column min-vh-100">
      <ApplicationContext.Provider
        value={{
          requester,
          alertHandler,
          authService: auth.current,
          user,
          orcid,
        }}
      >
        <Header siteName={config.app.name}>
          <Header.NavigationSection>
            <Header.NavigationItem exact to="/">
              <Icon
                style={{ verticalAlign: 'middle' }}
                path={mdiLinkVariant}
                size="1.3em"
              />{' '}
              Link now
            </Header.NavigationItem>
            {orcid.orcid_id && (
              <Header.NavigationItem exact to="/settings">
                <Icon
                  style={{ verticalAlign: 'middle' }}
                  path={mdiCogOutline}
                  size="1.3em"
                />{' '}
                Settings
              </Header.NavigationItem>
            )}
            {user.roles && checkAccess(user, searchRoles) && (
              <Header.NavigationItem exact to="/search">
                <Icon
                  style={{ verticalAlign: 'middle' }}
                  path={mdiMagnify}
                  size="1.3em"
                />{' '}
                Search
              </Header.NavigationItem>
            )}
            {user.roles && checkAccess(user, adminRoles) && (
              <Header.NavigationItem exact to="/admin">
                <Icon
                  style={{ verticalAlign: 'middle' }}
                  path={mdiStarOutline}
                  size="1.3em"
                />{' '}
                Admin
              </Header.NavigationItem>
            )}
            <Header.NavigationItem exact to="/about">
              <Icon
                style={{ verticalAlign: 'middle' }}
                path={mdiInformationOutline}
                size="1.3em"
              />{' '}
              About
            </Header.NavigationItem>
            <Header.NavigationItem exact to="/help">
              <Icon
                style={{ verticalAlign: 'middle' }}
                path={mdiHelpCircleOutline}
                size="1.3em"
              />{' '}
              Help
            </Header.NavigationItem>
          </Header.NavigationSection>
          <Header.NavigationSection className="ms-lg-auto">
            {!user.sub ? (
              <Header.NavigationItem
                to="#login"
                key="#login"
                className="nav-link"
                onClick={onLoginClick}
              >
                <Icon
                  style={{ verticalAlign: 'middle' }}
                  path={mdiLogin}
                  size="1.3em"
                />{' '}
                Login
              </Header.NavigationItem>
            ) : (
              <Header.NavigationItem
                to="#logout"
                key="#logout"
                className="nav-link"
                onClick={handleLogoutClick}
              >
                <Icon
                  style={{ verticalAlign: 'middle' }}
                  path={mdiLogout}
                  size="1.3em"
                />{' '}
                Log out {user.sub}
              </Header.NavigationItem>
            )}
          </Header.NavigationSection>
        </Header>
        <Container fluid="lg" className="my-2 my-md-4 flex-grow-1">
          {alertHandler.alerts &&
            alertHandler.alerts.map((alert, index) => {
              return (
                <Alert
                  dismissible
                  key={index}
                  variant={alert.type || 'info'}
                  onClose={() => {
                    alertHandler.setAlerts(currentAlerts => {
                      currentAlerts.splice(index, 1)
                      return [...currentAlerts]
                    })
                  }}
                >
                  {alert.message}
                </Alert>
              )
            })}
          <Switch>
            <Route
              exact
              path="/"
              render={() => (
                <Home
                  onLoginClick={onLoginClick}
                  onLogoutClick={handleLogoutClick}
                  onLinkOrcidClick={createOrcidLinkHandler({
                    forceLogout: true,
                  })}
                  onCreateOrcidClick={createOrcidLinkHandler({
                    queryString: 'create=true',
                    forceLogout: true,
                  })}
                />
              )}
            />
            <Route
              exact
              path="/settings"
              render={() => (
                <Settings
                  unlinkOrcid={unlinkOrcid}
                  checkOrcidState={checkOrcidState}
                />
              )}
            />
            <Route exact path="/search" render={() => <Search />} />
            <Route exact path="/admin" render={() => <Admin />} />
            <Route exact path="/about" component={About} />
            <Route exact path="/help" component={Help} />
            <Route component={NotFound} />
          </Switch>
        </Container>
      </ApplicationContext.Provider>
      <Footer>
        <Footer.SubContent>
          <Footer.LegalSection>
            v{config.app.version}
            <br />
            {config.env.development && user.sub && (
              <a
                href="#logout"
                onClick={e => handleLogoutClick(e, { force: true })}
              >
                Logout of JCU Services
              </a>
            )}
          </Footer.LegalSection>
        </Footer.SubContent>
      </Footer>
      {config.env.development && (
        <Debug object={{ user, orcid, alerts: alertHandler.alerts }} />
      )}
    </div>
  )
}

App.propTypes = {
  defaultUser: PropTypes.object,
  defaultOrcid: PropTypes.object,
  defaultAlertHandler: PropTypes.object,
  defaultAuth: PropTypes.object,
  _checkLoginState: PropTypes.func,
  _checkOrcidState: PropTypes.func,
  _handleLoginMessage: PropTypes.func,
  _handleOrcidMessage: PropTypes.func,
  _logoutHandler: PropTypes.func,
  _unlinkOrcid: PropTypes.func,
}

export { ApplicationContext }
export default App
